import { Trans, useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@parrot-software/design-system';

/**
 * Props for NoInvoicingConfirmationDialog component
 */
export interface NoInvoicingConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Confirmation dialog shown before applying the "No quiero facturar" option
 * to the current subscription block. Warns the user about the irreversible
 * impact for the current billing period.
 */
export const NoInvoicingConfirmationDialog = ({
  open,
  onOpenChange,
  onCancel,
  onConfirm,
  isSubmitting = false,
}: NoInvoicingConfirmationDialogProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[451px] font-sans">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('noInvoicingConfirm.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 text-muted-foreground">
            <p>
              <Trans
                components={{ bold: <strong className="font-semibold" /> }}
                i18nKey="invoiceData:noInvoicingConfirm.descriptionLine1"
              />
            </p>
            <p>
              <Trans
                components={{ bold: <strong className="font-semibold" /> }}
                i18nKey="invoiceData:noInvoicingConfirm.descriptionLine2"
              />
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting} onClick={onCancel}>
            {t('noInvoicingConfirm.cancelButton')}
          </AlertDialogCancel>
          <AlertDialogAction disabled={isSubmitting} onClick={onConfirm}>
            {t('noInvoicingConfirm.confirmButton')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
